import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";

interface Slide {
  emoji: string;
  titleKey: string;
  descKey: string;
}

const soloSlides: Slide[] = [
  { emoji: "🎬", titleKey: "tutorialSlides.solo.welcome", descKey: "tutorialSlides.solo.welcomeDesc" },
  { emoji: "⚙️", titleKey: "tutorialSlides.solo.filters", descKey: "tutorialSlides.solo.filtersDesc" },
  { emoji: "👆", titleKey: "tutorialSlides.solo.swiping", descKey: "tutorialSlides.solo.swipingDesc" },
  { emoji: "⭐", titleKey: "tutorialSlides.solo.starring", descKey: "tutorialSlides.solo.starringDesc" },
  { emoji: "🔖", titleKey: "tutorialSlides.solo.bookmarking", descKey: "tutorialSlides.solo.bookmarkingDesc" },
  { emoji: "👀", titleKey: "tutorialSlides.solo.seenIt", descKey: "tutorialSlides.solo.seenItDesc" },
  { emoji: "⏱️", titleKey: "tutorialSlides.solo.timer", descKey: "tutorialSlides.solo.timerDesc" },
  { emoji: "📺", titleKey: "tutorialSlides.solo.ads", descKey: "tutorialSlides.solo.adsDesc" },
  { emoji: "🏆", titleKey: "tutorialSlides.solo.winner", descKey: "tutorialSlides.solo.winnerDesc" },
];

const groupSlides: Slide[] = [
  { emoji: "👥", titleKey: "tutorialSlides.group.welcome", descKey: "tutorialSlides.group.welcomeDesc" },
  { emoji: "🏠", titleKey: "tutorialSlides.group.creating", descKey: "tutorialSlides.group.creatingDesc" },
  { emoji: "🔗", titleKey: "tutorialSlides.group.inviting", descKey: "tutorialSlides.group.invitingDesc" },
  { emoji: "👋", titleKey: "tutorialSlides.group.friends", descKey: "tutorialSlides.group.friendsDesc" },
  { emoji: "⚙️", titleKey: "tutorialSlides.group.filters", descKey: "tutorialSlides.group.filtersDesc" },
  { emoji: "👆", titleKey: "tutorialSlides.group.swiping", descKey: "tutorialSlides.group.swipingDesc" },
  { emoji: "⏱️", titleKey: "tutorialSlides.group.timer", descKey: "tutorialSlides.group.timerDesc" },
  { emoji: "📺", titleKey: "tutorialSlides.group.ads", descKey: "tutorialSlides.group.adsDesc" },
  { emoji: "🏆", titleKey: "tutorialSlides.group.winner", descKey: "tutorialSlides.group.winnerDesc" },
];

interface DiscoverTutorialSlidesProps {
  sessionType: "solo" | "group";
  onComplete: () => void;
}

const DiscoverTutorialSlides = ({ sessionType, onComplete }: DiscoverTutorialSlidesProps) => {
  const { t } = useTranslation();
  const slides = sessionType === "solo" ? soloSlides : groupSlides;
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(0);

  const goNext = () => {
    if (current === slides.length - 1) {
      onComplete();
    } else {
      setDirection(1);
      setCurrent((p) => p + 1);
    }
  };

  const goPrev = () => {
    if (current > 0) {
      setDirection(-1);
      setCurrent((p) => p - 1);
    }
  };

  const slide = slides[current];
  const isLast = current === slides.length - 1;

  return (
    <div className="h-[100dvh] bg-background flex items-center justify-center px-4 py-4">
      <div className="w-full max-w-md">
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="w-full bg-gradient-card border border-border rounded-2xl p-6 shadow-card flex flex-col"
        >
          {/* Progress dots */}
          <div className="flex items-center justify-center gap-1.5 mb-5">
            {slides.map((_, i) => (
              <div
                key={i}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === current
                    ? "w-6 bg-primary"
                    : i < current
                    ? "w-1.5 bg-primary/40"
                    : "w-1.5 bg-muted-foreground/20"
                }`}
              />
            ))}
          </div>

          {/* Slide content */}
          <div className="min-h-[240px] flex flex-col items-center justify-center text-center">
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={current}
                custom={direction}
                initial={{ opacity: 0, x: direction >= 0 ? 40 : -40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: direction >= 0 ? -40 : 40 }}
                transition={{ duration: 0.25 }}
                className="flex flex-col items-center gap-3"
              >
                <span className="text-5xl">{slide.emoji}</span>
                <h3 className="font-display text-xl font-bold text-foreground">
                  {t(slide.titleKey)}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed max-w-xs">
                  {t(slide.descKey)}
                </p>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-6">
            <button
              onClick={goPrev}
              disabled={current === 0}
              className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              {t("common.back")}
            </button>

            <span className="text-xs text-muted-foreground">
              {current + 1} / {slides.length}
            </span>

            <button
              onClick={goNext}
              className="flex items-center gap-1 text-sm font-semibold text-primary hover:text-primary/80 transition-colors"
            >
              {isLast ? t("tutorialSlides.letsStart") : t("common.next")}
              {!isLast && <ChevronRight className="w-4 h-4" />}
            </button>
          </div>

          {/* Skip */}
          <button
            onClick={onComplete}
            className="mt-4 text-xs text-muted-foreground hover:text-foreground transition-colors text-center"
          >
            {t("tutorialSlides.skipTutorial")}
          </button>
        </motion.div>
      </div>
    </div>
  );
};

export default DiscoverTutorialSlides;
